import usePageMeta from "@/lib/usePageMeta";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import TestimonialsWall from "@/components/TestimonialsWall";
import { GiftGuideStrip } from "@/components/GiftGuideStrip";
import ReviewPhotoWall from "@/components/reviews/ReviewPhotoWall";
import { ArrowRight, FishSimple, TShirt, Lightning, Question } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1522911715181-6ce196f07c76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxmaXNoaW5nJTIwYm9hdCUyMHN1bnJpc2V8ZW58MHx8fHwxNzkxNDc2MTU1fDA&ixlib=rb-4.1.0&q=85&w=1800&auto=format";
const LIFESTYLE = "https://images.unsplash.com/photo-1611954268683-6a7fab18afdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxmaXNoZXJtYW4lMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzkxNDc2MTU1fDA&ixlib=rb-4.1.0&q=85&w=1000&auto=format";
const TEE = "https://images.unsplash.com/photo-1622445275463-afa2ab738c34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHx0LXNoaXJ0JTIwbW9ja3VwfGVufDB8fHx8MTc5MTQ3NjE1NXww&ixlib=rb-4.1.0&q=85&w=1000&auto=format";

export default function Home() {
  usePageMeta({ title: "Reel Funny Goods — Funny Fishing Shirts, Hoodies, Mugs & Gifts", path: "/" });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/products", { params: { limit: 8, shuffle: true, _: Date.now() } })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page">
      <GiftGuideStrip />
      {/* HERO */}
      <section className="relative min-h-[66vh] overflow-hidden">
        <img src={HERO} alt="Sunrise on the lake" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-14 min-h-[66vh] flex flex-col justify-end">
          <div className="max-w-3xl text-white">
            <div className="inline-flex items-center gap-2 border border-white/40 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.25em] mb-6">
              <Lightning size={12} weight="fill" className="text-[#F59E0B]" />
              Printed on demand
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
              Reel in <span className="text-[#F59E0B]">humor</span>.<br />
              Wear your catch.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/85">
              Shamelessly funny shirts, mugs & gear for anglers who take fishing seriously
              and themselves not at all.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="btn-primary rounded-sm px-7 h-12 inline-flex items-center font-display tracking-wide"
              >
                Shop the catch <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link
                to="/shop"
                className="rounded-sm px-7 h-12 inline-flex items-center font-display tracking-wide border border-white/50 text-white hover:bg-white hover:text-foreground transition-colors"
              >
                Browse collections
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY BENTO */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Collections</div>
            <h2 className="font-display text-4xl md:text-5xl mt-2">Pick your bait.</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-widest hover:text-[#C84A31] transition-colors">
            All products <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Link
            to="/shop"
            className="group relative md:row-span-2 h-80 md:h-auto overflow-hidden rounded-sm border border-border"
          >
            <img src={LIFESTYLE} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C211E] via-[#1C211E]/30 to-transparent" />
            <div className="relative h-full p-7 flex flex-col justify-end text-white">
              <FishSimple size={28} weight="duotone" className="text-[#F59E0B] mb-3" />
              <div className="font-display text-3xl">Shop All Designs</div>
              <div className="text-sm text-white/70 mt-1">Tees, caps, mugs & more.</div>
            </div>
          </Link>
          <div data-testid="home-quiz-tile" className="relative md:col-span-2 h-56 overflow-hidden rounded-sm border border-border bg-[#2A4B3C] text-white p-6 flex flex-col justify-between">
            <Question size={24} weight="duotone" className="text-[#F59E0B]" />
            <div>
              <div className="font-display text-2xl">Humor Quiz</div>
              <div className="text-sm text-white/70 mt-1">3 questions. Find your shirt.</div>
              <Link to="/quiz" className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#F59E0B] hover:text-white transition-colors">
                Take it <ArrowRight size={12} />
              </Link>
            </div>
          </div>
          <Link
            to="/shop"
            className="group relative md:col-span-2 h-56 overflow-hidden rounded-sm border border-border bg-white"
          >
            <img src={TEE} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/50 to-transparent" />
            <div className="relative h-full p-7 flex flex-col justify-center max-w-sm">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">New arrivals</div>
              <div className="font-display text-3xl mt-2">Fresh prints, straight off the dock.</div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#C84A31]">
                See the lineup <ArrowRight size={14} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-6 pb-10">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display text-3xl md:text-4xl">Hooked right now</h2>
          <Link to="/shop" className="text-sm uppercase tracking-widest hover:text-[#C84A31] transition-colors">
            View all →
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-square bg-muted animate-pulse rounded-sm" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div data-testid="home-empty" className="border border-dashed border-border rounded-sm p-16 text-center">
            <FishSimple size={40} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
            <div className="font-display text-xl">No products yet</div>
            <p className="text-sm text-muted-foreground mt-2">
              Admin: head to <Link to="/admin" className="text-[#C84A31] underline underline-offset-4">the dashboard</Link> and sync your Printify shop.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {products.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      <ReviewPhotoWall />
      <TestimonialsWall />
    </div>
  );
}
